import { useQuery } from "@tanstack/react-query";
import {
  Download,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  Presentation,
  FileArchive,
  File as FileIcon,
} from "lucide-react";
import {
  listEventAttachments,
  getAttachmentPublicUrl,
  formatBytes,
  fileCategoryFor,
  type EventAttachment,
} from "@/lib/event-attachments";

function CategoryIcon({ mime }: { mime: string }) {
  const cat = fileCategoryFor(mime);
  const cls = "h-5 w-5 shrink-0 text-primary";
  switch (cat) {
    case "pdf":
      return <FileText className={cls} />;
    case "image":
      return <ImageIcon className={cls} />;
    case "sheet":
      return <FileSpreadsheet className={cls} />;
    case "slides":
      return <Presentation className={cls} />;
    case "zip":
      return <FileArchive className={cls} />;
    case "doc":
      return <FileText className={cls} />;
    default:
      return <FileIcon className={cls} />;
  }
}

/**
 * Seção pública "Materiais para download" na página do evento.
 * Download é direto do CDN (URL pública do Storage) — zero carga no app server.
 * Some por completo quando não há anexos.
 */
export function EventAttachmentsDownload({ eventId }: { eventId: string }) {
  const { data: attachments = [] } = useQuery({
    queryKey: ["event-attachments", eventId],
    queryFn: () => listEventAttachments(eventId),
  });

  if (attachments.length === 0) return null;

  return (
    <div className="border-t border-border pt-6 space-y-3">
      <h3 className="font-display font-semibold text-foreground">Materiais para download</h3>
      <ul className="space-y-2">
        {attachments.map((a: EventAttachment) => (
          <li key={a.id}>
            <a
              href={getAttachmentPublicUrl(a.storage_path)}
              download={a.file_name}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-md border border-border/60 bg-card px-3 py-2 transition-colors hover:border-primary/50 hover:bg-muted/40"
            >
              <CategoryIcon mime={a.mime_type} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground" title={a.file_name}>
                  {a.file_name}
                </p>
                <p className="text-xs text-muted-foreground">{formatBytes(a.file_size_bytes)}</p>
              </div>
              <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
